import React, { useState } from "react";
import { Form } from "react-final-form";
import { useSelector } from "react-redux";
import { Box, Button, Divider, FormControl, FormLabel, Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { FormApi } from "final-form";
import { useSnackbar } from "notistack";
import DualSided from "svg_icons/planner-extended-data-prep/dual_sided.svg?react";
import DualSidedMidStreetCrossings from "svg_icons/planner-extended-data-prep/dual_sided_mid_street_crossings.svg?react";
import SingleSided from "svg_icons/planner-extended-data-prep/single_sided.svg?react";
import SingleSidedMinimizeDropCrossings from "svg_icons/planner-extended-data-prep/single_sided_minimize_drop_crossings.svg?react";

import { useGetVersionQuery, useUpdateVersionArchitectureMutation } from "fond/api";
import { CheckboxField, SwitchField } from "fond/form/fields";
import RadioField from "fond/form/fields/RadioField";
import { Store } from "fond/types";
import { useStackedNavigationContext } from "fond/widgets";

interface IFormData {
  serviceRoads: boolean;
  cablePreference: "single_sided" | "dual_sided";
  midStreetCrossings: boolean;
  minimizeDropCrossings: boolean;
}

const getPreferenceDescription = (cablePreference: string, midStreetCrossings: boolean, minimizeDropCrossings: boolean) => {
  if (cablePreference === "dual_sided") {
    return midStreetCrossings
      ? {
          heading: "Dual-sided cables, Include mid-street crossings",
          subtitle: "Drop cables may cross mid-street. (requires permits)",
          icon: <DualSidedMidStreetCrossings />,
        }
      : {
          heading: "Dual-sided cables",
          subtitle: "Drop cables only cross at intersections.",
          icon: <DualSided />,
        };
  }

  return minimizeDropCrossings
    ? {
        heading: "Single-sided cables, Minimize drop crossings",
        subtitle: "Drop cables cross once per hub.",
        icon: <SingleSidedMinimizeDropCrossings />,
      }
    : {
        heading: "Single-sided cables",
        subtitle: "Drop cables may cross multiple times per hub.",
        icon: <SingleSided />,
      };
};

const PreferenceDescription: React.FC<IFormData> = ({ cablePreference, midStreetCrossings, minimizeDropCrossings }) => {
  const { heading, subtitle, icon } = getPreferenceDescription(cablePreference, midStreetCrossings, minimizeDropCrossings);
  return (
    <Box display="flex" flexDirection="column" my={2}>
      {icon}
      <Typography variant="body1" fontWeight="bold" mt={2}>
        {heading}
      </Typography>
      <Typography variant="body1" color="textSecondary">
        {subtitle}
      </Typography>
    </Box>
  );
};

const DataPrepSettingsPanel: React.FC = () => {
  const { goBack } = useStackedNavigationContext();
  const [isSaving, setIsSaving] = useState(false);
  const versionId = useSelector((state: Store) => state.project.versionId);
  const { data: version } = useGetVersionQuery(versionId ?? skipToken, { skip: !versionId });
  const [updateVersionArchitecture] = useUpdateVersionArchitectureMutation();
  const { enqueueSnackbar } = useSnackbar();

  const onSubmit = async (values: IFormData, form: FormApi<IFormData>) => {
    if (!versionId) {
      enqueueSnackbar("Unable to save Data Prep Settings.", { variant: "error" });
      return;
    }
    setIsSaving(true);

    // Handle form submission
    const payload = {
      IncludeServiceRoads: values.serviceRoads,
      CablePreference: values.cablePreference,
      // `IncludeMidStreetCrossings` and `MinimizeDropCrossings` are only relevant for dual-sided and single-sided
      // cables respectively, however we still include their value in the payload for simplicity.
      IncludeMidStreetCrossings: values.midStreetCrossings,
      MinimizeDropCrossings: values.minimizeDropCrossings,
    };

    try {
      await updateVersionArchitecture({ versionId, architecture: { DataPrepConfiguration: payload } }).unwrap();
      enqueueSnackbar("Data Prep Settings saved successfully.", { variant: "success" });
      goBack();
    } catch {
      enqueueSnackbar("Failed to save Data Prep Settings.", { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    goBack();
  };

  return (
    <Box px={1}>
      <Form<IFormData>
        initialValues={{
          serviceRoads: version?.Architecture?.DataPrepConfiguration?.IncludeServiceRoads ?? false,
          cablePreference: version?.Architecture?.DataPrepConfiguration?.CablePreference ?? "dual_sided",
          midStreetCrossings: version?.Architecture?.DataPrepConfiguration?.IncludeMidStreetCrossings ?? false,
          minimizeDropCrossings: version?.Architecture?.DataPrepConfiguration?.MinimizeDropCrossings ?? false,
        }}
        onSubmit={onSubmit}
        render={({ handleSubmit, values }) => {
          return (
            <form onSubmit={handleSubmit} id="data-prep-settings-form">
              <Box display="flex" gap="4px" flexDirection="column">
                <Box display="flex" justifyContent="space-between" flexDirection="column">
                  <Box width="100%" display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
                    <FormLabel htmlFor="serviceRoads">Include Service Roads</FormLabel>
                    <SwitchField
                      name="serviceRoads"
                      label=""
                      data-testid="serviceRoads"
                      fieldProps={{
                        type: "checkbox",
                        checked: values.serviceRoads,
                      }}
                    />
                  </Box>
                  <Typography variant="caption" color="textSecondary">
                    Service roads represent parking aisles, driveways, alleys, and similar routes.
                  </Typography>
                  <Divider sx={{ my: 2 }} />

                  <Typography fontWeight="500" mb={3} id="cables-preference-label">
                    Cables Preference
                  </Typography>

                  <Box width="100%">
                    <FormControl>
                      <RadioField name="cablePreference" value="dual_sided" label="Dual-sided Cables" size="small" />
                      <CheckboxField
                        dataTestId="mid-street-crossings"
                        sxProps={{ ".MuiFormControlLabel-label": { fontSize: 13 }, ml: 1 }}
                        name="midStreetCrossings"
                        size="small"
                        color="primary"
                        label="Include mid-street crossings"
                        disabled={values.cablePreference !== "dual_sided"}
                        checked={values.midStreetCrossings}
                      />
                      <RadioField name="cablePreference" value="single_sided" label="Single-sided Cables" size="small" />
                      <CheckboxField
                        dataTestId="min-drop-crossings"
                        sxProps={{ ".MuiFormControlLabel-label": { fontSize: 13 }, ml: 1 }}
                        name="minimizeDropCrossings"
                        size="small"
                        color="primary"
                        label="Minimize drop crossings"
                        disabled={values.cablePreference !== "single_sided"}
                        checked={values.minimizeDropCrossings}
                      />
                    </FormControl>
                  </Box>
                  <PreferenceDescription {...values} />
                </Box>
              </Box>
            </form>
          );
        }}
      />
      <Divider />
      <Box display="flex" alignItems="center" justifyContent="flex-end" mt={2}>
        <Button color="primary" size="small" onClick={handleBack} data-testid="cancel-button" disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          sx={{ ml: 1, px: 2 }}
          data-testid="apply-button"
          type="submit"
          form="data-prep-settings-form"
          disabled={!versionId || isSaving}
        >
          Apply
        </Button>
      </Box>
    </Box>
  );
};

export default DataPrepSettingsPanel;
